/**
 * API Configuration
 * Provides a clean API base URL, handling any trailing whitespace or newlines
 * that might be present in environment variables.
 */

export const getApiBaseUrl = (): string => {
  const raw = (import.meta.env.VITE_API_BASE_URL || '').trim()

  // Helper to safely normalize a URL (or return empty string)
  const normalize = (u: string): string => (u ? u.replace(/\/+$/, '') : '')
  
  // Helper to ensure HTTPS in production (prevents mixed content errors)
  const ensureHttps = (url: string): string => {
    if (!url) return url
    // In production, always use HTTPS to prevent mixed content errors
    if (import.meta.env.PROD && url.startsWith('http://')) {
      return url.replace('http://', 'https://')
    }
    return url
  }

  // In production, use the configured API URL (Firebase Hosting)
  if (import.meta.env.PROD) {
    const candidate = normalize(raw)
    try {
      if (candidate) {
        const host = new URL(candidate).hostname
        const isRunApp = host.endsWith('.run.app') || host.endsWith('.a.run.app')
        if (!isRunApp) {
          // Ensure HTTPS in production
          return ensureHttps(candidate)
        }
        // Fall through to safe production default below
        // eslint-disable-next-line no-console
        console.warn('[api] Overriding run.app base URL; using configured API URL')
      }
    } catch {
      // Invalid URL in env; fall back below
    }

    // Staging: always use same-origin /api routes (Firebase Hosting → Cloud Run proxy).
    if (import.meta.env.VITE_ENVIRONMENT === 'staging') {
      return ''
    }

    // Return the configured API URL for production (Firebase Hosting)
    // Always ensure HTTPS to prevent mixed content errors
    return ensureHttps(normalize(raw) || 'https://api.livewello.com')
  }

  // Non‑production: default to local backend if not provided
  return normalize(raw) || 'http://localhost:8000'
}

export const API_BASE_URL = getApiBaseUrl()

/**
 * Base URL for Server-Sent Events endpoints.
 *
 * Firebase Hosting buffers streaming responses through its CDN, so an SSE call
 * routed via the same-origin /api proxy delivers nothing until the stream ends
 * and then fails with a 502. Streaming calls therefore go straight to Cloud Run,
 * which the backend's CORS config already allows for this origin.
 *
 * Only streaming endpoints need this; everything else stays on the proxy so
 * same-origin behaviour is unchanged.
 */
export const getStreamingBaseUrl = (): string => {
  const direct = (import.meta.env.VITE_STREAMING_BASE_URL || '').trim().replace(/\/+$/, '')
  if (direct) return direct
  return API_BASE_URL
}

export const STREAMING_BASE_URL = getStreamingBaseUrl()

export const getMcpUrl = (): string => {
  const raw = (import.meta.env.VITE_MCP_URL || '').trim().replace(/\/+$/, '')
  if (raw) return raw
  return import.meta.env.PROD
    ? 'https://mcp.livewello.com/mcp'
    : 'http://localhost:8002/mcp'
}

export const MCP_URL = getMcpUrl()
